import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import analyticsApi, { PublishedPostRow } from '../api/analyticsApi';

// Per-brand bulk post-metrics cache for the tended cards. A brand page can
// mount dozens of TendV2ContentCards in one pass (13 channel strips ×
// several cards); each card doing its own metrics round-trip is the exact
// connection-pool incident PublishStatusContext documents. Every card calls
// useBrandPostMetrics(brandId) and the provider makes ONE
// GET /analytics/brands/{id}/posts per brand per TTL window.
//
// The endpoint returns one row per successful publish, newest first — the
// map keeps the FIRST row per tendv2 content id (= latest publish), same
// dedupe as usePostMetrics. Missing id or metrics_available:false =
// metrics pending, never zeros.

interface BrandMetricsEntry {
  byContentId: Record<number, PublishedPostRow>;
  fetchedAt: number;
  loading: boolean;
}

interface PostMetricsContextType {
  entries: Record<number, BrandMetricsEntry>;
  /** Idempotent: joins an in-flight fetch or serves the TTL cache. */
  ensure: (brandId: number) => void;
  /** Force refetch (e.g. right after an in-card publish). */
  refresh: (brandId: number) => void;
}

const CACHE_TTL = 60000; // snapshots land on a poller cadence; 60s parity with PublishStatusContext

const PostMetricsContext = createContext<PostMetricsContextType | null>(null);

export const PostMetricsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<Record<number, BrandMetricsEntry>>({});
  const inFlightRef = useRef<Set<number>>(new Set());
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const fetchBrand = useCallback(async (brandId: number) => {
    if (inFlightRef.current.has(brandId)) return;
    inFlightRef.current.add(brandId);
    setEntries((prev) => ({
      ...prev,
      [brandId]: {
        byContentId: prev[brandId]?.byContentId || {},
        fetchedAt: prev[brandId]?.fetchedAt || 0,
        loading: true,
      },
    }));
    try {
      const response = await analyticsApi.getBrandPosts(brandId);
      const map: Record<number, PublishedPostRow> = {};
      for (const post of response.posts) {
        if (post.tend_content_id != null && !(post.tend_content_id in map)) {
          map[post.tend_content_id] = post; // newest-first → first = latest publish
        }
      }
      setEntries((prev) => ({
        ...prev,
        [brandId]: { byContentId: map, fetchedAt: Date.now(), loading: false },
      }));
    } catch (err) {
      // Non-fatal (public share page gets a 401 here): empty map = no
      // analytics row, cards render exactly as before.
      console.debug('post metrics fetch failed:', err);
      setEntries((prev) => ({
        ...prev,
        [brandId]: {
          byContentId: prev[brandId]?.byContentId || {},
          fetchedAt: Date.now(),
          loading: false,
        },
      }));
    } finally {
      inFlightRef.current.delete(brandId);
    }
  }, []);

  const ensure = useCallback(
    (brandId: number) => {
      const entry = entriesRef.current[brandId];
      if (entry && (entry.loading || Date.now() - entry.fetchedAt < CACHE_TTL)) return;
      fetchBrand(brandId);
    },
    [fetchBrand]
  );

  const refresh = useCallback(
    (brandId: number) => {
      fetchBrand(brandId);
    },
    [fetchBrand]
  );

  const value = useMemo(() => ({ entries, ensure, refresh }), [entries, ensure, refresh]);

  return <PostMetricsContext.Provider value={value}>{children}</PostMetricsContext.Provider>;
};

const EMPTY: Record<number, PublishedPostRow> = {};

export interface BrandPostMetrics {
  byContentId: Record<number, PublishedPostRow>;
  refresh: () => void;
}

/** Safe to call from every card mount — one request per brand per TTL. */
export const useBrandPostMetrics = (brandId?: number | null): BrandPostMetrics => {
  const context = useContext(PostMetricsContext);
  if (!context) {
    throw new Error('useBrandPostMetrics must be used within PostMetricsProvider');
  }
  const { entries, ensure, refresh } = context;

  useEffect(() => {
    if (brandId) ensure(brandId);
  }, [brandId, ensure]);

  const byContentId = (brandId && entries[brandId]?.byContentId) || EMPTY;
  const refreshBrand = useCallback(() => {
    if (brandId) refresh(brandId);
  }, [brandId, refresh]);

  return { byContentId, refresh: refreshBrand };
};

export default PostMetricsContext;
